// Client networking: one socket, snapshots buffered and played back slightly in the past so
// other players glide instead of teleporting.
import type { CharacterState, ClientMsg, EntityState, ServerMsg, ZoneInfo } from '../../../packages/protocol/src/index.ts';

const DELAY = 0.12;   // seconds of interpolation buffer

interface Track { id: string; buf: { t: number; e: EntityState }[]; last: EntityState }

export interface NetHooks {
  welcome(you: string, char: CharacterState, zone: ZoneInfo): void;
  char(char: CharacterState): void;
  chat(from: string, ch: string, text: string): void;
  note(text: string, kind?: string): void;
  hit(src: string, dst: string, dmg: number, crit?: boolean, kill?: boolean): void;
  denied(why: string): void;
  status(s: 'connecting' | 'online' | 'offline'): void;
}

export class Net {
  ws: WebSocket | null = null;
  you = '';
  tracks = new Map<string, Track>();
  ping = 0;
  sent = 0;
  private hooks: NetHooks;
  private url: string;
  private seq = 1;
  private retry = 0;
  private token: string | null = null;
  private creds: { name: string; pass: string; cls?: string; ink?: string } | null = null;
  private clock = 0;

  constructor(url: string, hooks: NetHooks) { this.url = url; this.hooks = hooks; }

  login(name: string, pass: string, cls: string, ink: string) {
    this.creds = { name, pass, cls, ink };
    this.token = null;
    this.open();
  }
  resume(token: string) { this.token = token; this.open(); }

  private open() {
    this.hooks.status('connecting');
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => {
      this.retry = 0;
      if (this.token) this.send({ t: 'resume', token: this.token });
      else if (this.creds) this.send({ t: 'auth', name: this.creds.name, pass: this.creds.pass, cls: this.creds.cls as never, ink: this.creds.ink });
      setInterval(() => this.send({ t: 'ping', s: performance.now() }), 4000);
    };
    ws.onmessage = (ev) => {
      let m: ServerMsg;
      try { m = JSON.parse(ev.data as string) as ServerMsg; } catch { return; }
      this.handle(m);
    };
    ws.onclose = () => {
      this.hooks.status('offline');
      if (!this.token && !this.creds) return;
      const wait = Math.min(8000, 600 * 2 ** this.retry++);
      setTimeout(() => this.open(), wait);
    };
    ws.onerror = () => { /* onclose does the retry */ };
  }

  send(m: ClientMsg) { if (this.ws && this.ws.readyState === 1) { this.sent++; this.ws.send(JSON.stringify(m)); } }

  input(dx: number, dy: number, run: boolean) { this.send({ t: 'input', seq: this.seq++, dx, dy, run }); }

  private handle(m: ServerMsg) {
    switch (m.t) {
      case 'welcome':
        this.you = m.you; this.token = m.token;
        try { localStorage.setItem('ink:token', m.token); localStorage.setItem('ink:name', m.char.name); } catch { /* private mode */ }
        this.hooks.status('online');
        this.hooks.welcome(m.you, m.char, m.zone);
        break;
      case 'denied':
        this.token = null; this.creds = null;
        try { localStorage.removeItem('ink:token'); } catch { /* ignore */ }
        this.hooks.denied(m.why);
        break;
      case 'snap': {
        this.clock = performance.now() / 1000;
        for (const e of m.ents) {
          let tr = this.tracks.get(e.id);
          if (!tr) { tr = { id: e.id, buf: [], last: e }; this.tracks.set(e.id, tr); }
          tr.buf.push({ t: this.clock, e });
          if (tr.buf.length > 12) tr.buf.shift();
          tr.last = e;
        }
        const seen = new Set(m.ents.map(e => e.id));
        for (const [id, tr] of this.tracks) {
          if (m.gone.includes(id)) { this.tracks.delete(id); continue; }
          // entity left our area of interest: drop it once its buffer is stale
          if (!seen.has(id) && this.clock - (tr.buf[tr.buf.length - 1]?.t ?? 0) > 1.2) this.tracks.delete(id);
        }
        break;
      }
      case 'char': this.hooks.char(m.char); break;
      case 'chat': this.hooks.chat(m.from, m.ch, m.text); break;
      case 'note': this.hooks.note(m.text, m.kind); break;
      case 'hit': this.hooks.hit(m.src, m.dst, m.dmg, m.crit, m.kill); break;
      case 'pong': this.ping = performance.now() - m.s; break;
    }
  }

  /** entities as they should look right now, interpolated */
  view(): EntityState[] {
    const t = performance.now() / 1000 - DELAY;
    const out: EntityState[] = [];
    for (const tr of this.tracks.values()) {
      const b = tr.buf;
      if (b.length === 0) continue;
      if (b.length === 1 || t <= b[0]!.t) { out.push(b[0]!.e); continue; }
      let a = b[0]!, c = b[b.length - 1]!;
      for (let i = 0; i < b.length - 1; i++) if (b[i]!.t <= t && b[i + 1]!.t >= t) { a = b[i]!; c = b[i + 1]!; break; }
      if (t > c.t) { out.push(c.e); continue; }
      const k = c.t > a.t ? (t - a.t) / (c.t - a.t) : 1;
      out.push({
        ...c.e,
        x: a.e.x + (c.e.x - a.e.x) * k,
        y: a.e.y + (c.e.y - a.e.y) * k,
        a: angLerp(a.e.a, c.e.a, k),
      });
    }
    return out;
  }
}

function angLerp(a: number, b: number, k: number) {
  let d = ((b - a + Math.PI) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2) - Math.PI;
  return a + d * k;
}
